import { gql, NetworkStatus } from '@apollo/client';
import {
  Payment_Batches_Bool_Exp,
  Payments_GetPaymentHistoryListQuery,
  Payments_GetPaymentHistoryListQueryVariables,
} from '@/types';
import { PaymentBatchResultCode } from '@/enums';
import { useMemo } from 'react';
import { useUserContext } from '@/contexts/user-context';
import { useWrappedQuery } from './use-wrapped-query';

/*
 These result codes are returned from Adyen when processing payments.
 Since we do not clean up payment batches with failed result codes
 when submitting new payments for the same invoices, we will hide these
 payment batches from transaction history.

 Docs: https://docs.adyen.com/online-payments/payment-result-codes
*/
export const FAILED_PAYMENT_RESULT_CODES = [
  PaymentBatchResultCode.CANCELLED,
  PaymentBatchResultCode.ERROR,
  PaymentBatchResultCode.REFUSED,
];
export const PAYMENT_HISTORY_PAGE_LIMIT = 10;

export const PAYMENT_HISTORY_QUERY = gql`
  fragment PaymentHistoryListFields on payment_batches {
    created_at
    customer_facing_id
    link_paid_at
    payment_batch_amount {
      charged_amount_in_micros
      payment_batch_uuid
    }
    status
    uuid
  }

  query Payments_GetPaymentHistoryList(
    $limit: Int!
    $offset: Int
    $where: payment_batches_bool_exp!
  ) {
    payment_batches(
      limit: $limit
      offset: $offset
      order_by: { created_at: desc }
      where: $where
    ) {
      ...PaymentHistoryListFields
    }

    payment_batches_aggregate(where: $where) {
      aggregate {
        count
      }
    }
  }
`;

export const useGetPaymentHistoryQuery = () => {
  const { selectedRestaurantRelationship, selectedRestaurantUUID } =
    useUserContext();

  const whereVariables: Payment_Batches_Bool_Exp = useMemo(
    () => ({
      _or: [
        // Paid pay by links
        {
          link_paid_at: { _is_null: false },
          payment_due_at: { _is_null: false },
        },
        // Successful custom payments
        {
          payment_due_at: { _is_null: true },
          result_code: { _nin: FAILED_PAYMENT_RESULT_CODES },
        },
      ],
      // Check scanner batches created from the Jack Henry webhook have no
      // restaurant link, only payor.
      payor_uuid: { _eq: selectedRestaurantUUID },
      supplier_uuid: {
        _eq: selectedRestaurantRelationship.supplierUUID,
      },
    }),
    [selectedRestaurantRelationship, selectedRestaurantUUID],
  );

  const {
    data,
    error,
    fetchMore,
    hasResponse,
    loading,
    networkStatus,
    refetch,
  } = useWrappedQuery<
    Payments_GetPaymentHistoryListQuery,
    Payments_GetPaymentHistoryListQueryVariables
  >(PAYMENT_HISTORY_QUERY, {
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
    variables: {
      limit: PAYMENT_HISTORY_PAGE_LIMIT,
      where: whereVariables,
    },
  });

  const fetchMoreOffset = useMemo(() => {
    const currentCount = data?.payment_batches.length ?? 0;
    const totalCount = data?.payment_batches_aggregate?.aggregate?.count ?? 0;
    return currentCount < totalCount ? currentCount : null;
  }, [data]);

  return useMemo(() => {
    return {
      error,
      fetchingMore: networkStatus === NetworkStatus.fetchMore,
      fetchMore: async () => {
        if (typeof fetchMoreOffset === 'number') {
          await fetchMore<
            Payments_GetPaymentHistoryListQuery,
            Payments_GetPaymentHistoryListQueryVariables
          >({
            updateQuery: (prevResult, { fetchMoreResult }) => {
              return fetchMoreResult
                ? {
                    ...prevResult,
                    payment_batches: [
                      ...prevResult.payment_batches,
                      ...fetchMoreResult.payment_batches,
                    ],
                  }
                : prevResult;
            },
            variables: {
              limit: PAYMENT_HISTORY_PAGE_LIMIT,
              offset: fetchMoreOffset,
              where: whereVariables,
            },
          });
        }
      },
      hasResponse,
      loading,
      paymentBatches: data?.payment_batches || [],
      refresh: async () => {
        await refetch();
      },
      refreshing: networkStatus === NetworkStatus.refetch,
    };
  }, [
    data,
    error,
    fetchMore,
    fetchMoreOffset,
    hasResponse,
    loading,
    networkStatus,
    refetch,
    whereVariables,
  ]);
};
